import React from "react";
import { Table, Form, OverlayTrigger, Tooltip } from "react-bootstrap";

export const CartTable = ({
	t,
	cart,
	getCurrencySymbol,
	onBatchChange,
	onPriceChange,
	onQuantityChange,
	onRemoveItem,
	disableScrollProps,
	updateReturnQuantity,
	itemTaxes,
	posTaxType,
	pharmacyStateCode,
	patientStateCode,
	totals
}) => {
	const isSameState =
		!patientStateCode ||
		!pharmacyStateCode ||
		pharmacyStateCode === patientStateCode;

	// Calculate bill-level discount rate based on total taxable value
	const totalTaxableBeforeDiscount = cart.reduce((sum, item) => {
		const itemKey = `${item._id}-${item.selectedBatchId}`;
		const taxInfo = itemTaxes[itemKey];
		if (!taxInfo) return sum;
		const returnQuantity = item.returnQuantity || 0;
		if (returnQuantity <= 0) return sum;
		const proportion = returnQuantity / (item.saleQuantity || item.quantity || 1);
		return sum + (taxInfo.taxableAmount || 0) * proportion;
	}, 0);

	const billDiscount = totals?.discount || 0;
	const billDiscountRate =
		totalTaxableBeforeDiscount > 0 ? billDiscount / totalTaxableBeforeDiscount : 0;

	return (
		<div className="table-responsive table-wrap">
			<Table className="align-middle rounded shadow-sm table-bordered table-sm" style={{ fontSize: 'smaller' }}>
				<thead>
					<tr>
						<th scope="col" className="text-nowrap">{t("Batch Number")}</th>
						<th scope="col">{t("Product")}</th>
						<th scope="col">{t("Manufacturer")}</th>
						<th scope="col" className="text-center">{t("Price")}</th>
						<th scope="col" className="text-center">{t("Qty")}</th>
						<th scope="col" className="text-center">{t("Return Qty")}</th>
						<th scope="col" className="text-center">{t("Tax %")}</th>
						<th scope="col" className="text-center text-wrap">{t("Total Taxable Value")}</th>
						{posTaxType === "gst" ? (
							isSameState ? (
								<>
									<th scope="col" className="text-center">{t("CGST")}</th>
									<th scope="col" className="text-center">{t("SGST")}</th>
								</>
							) : (
								<th scope="col" className="text-center">{t("IGST")}</th>
							)
						) : (
							<th scope="col" className="text-center">{t("VAT")}</th>
						)}

						<th scope="col" className="text-center">{t("Gross Total")}</th>
						<th scope="col" className="text-center"></th>
					</tr>
				</thead>
				<tbody>
					{cart.map((item, rowIndex) => {
						const itemKey = `${item._id}-${item.selectedBatchId}`;
						const returnQuantity = item.returnQuantity || 0;
						const proportion = returnQuantity / (item.saleQuantity || item.quantity || 1);
						const taxInfo = itemTaxes[itemKey] || {};
						const taxRate = taxInfo.taxPercentage || 0;

						const taxableBeforeDiscount =
							(taxInfo.taxableAmount || 0) * (returnQuantity > 0 ? proportion : 0);

						const taxableAfterDiscount =
							taxableBeforeDiscount - taxableBeforeDiscount * billDiscountRate;

						let rowTax = 0;
						let rowCGST = 0;
						let rowSGST = 0;
						let rowIGST = 0;
						let rowVAT = 0;

						if (posTaxType === "gst") {
							if (isSameState) {
								rowTax = (taxableAfterDiscount * taxRate) / 100;
								rowCGST = rowTax / 2;
								rowSGST = rowTax / 2;
							} else {
								rowTax = (taxableAfterDiscount * taxRate) / 100;
								rowIGST = rowTax;
							}
						} else if (posTaxType === "vat") {
							rowVAT = (taxableAfterDiscount * taxRate) / 100;
							rowTax = rowVAT;
						}

						const grossTotal = taxableAfterDiscount + rowTax;
						return (
							<tr key={itemKey}>
								<td>
									<Form.Select
										size="sm"
										value={item.selectedBatchId || item.stockId || ""}
										onChange={(event) =>
											onBatchChange(item._id, event.target.value)
										}
										disabled
									>
										{item.batchList && item.batchList.length > 0 ? (
											item.batchList.map((batch, index) => (
												<option key={`${batch._id}-${index}`} value={batch._id}>
													{batch.batchNumber}
												</option>
											))
										) : (
											<option value={item.stockId || ""}>
												{item.batchNumber || "N/A"}
											</option>
										)}
									</Form.Select>
								</td>
								<td className="table-product-title" style={{ maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									<OverlayTrigger
										placement="top"
										overlay={<Tooltip id={`tooltip-saltName-${item._id}`}>{item.brandName}</Tooltip>}
									>
										<span>{item.saltName}</span>
									</OverlayTrigger>
								</td>
								<td className="table-product-title" style={{ maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									<OverlayTrigger
										placement="top"
										overlay={<Tooltip id={`tooltip-${item._id}`}>{item.manufacturer || "N/A"}</Tooltip>}
									>
										<span>{item.manufacturer}</span>
									</OverlayTrigger>
								</td>
								<td>
									<div className="input-group custom-qty-input table-price-counter-nowrap">
										<button
											type="button"
											className="button-minus icon-shape icon-sm btn-light"
											onClick={() =>
												onPriceChange(item._id, item.selectedBatchId, "salePrice", Math.max(0, (item.salePrice || 0) - 0.01))
											}
											disabled
										>
											-
										</button>
										<input
											type="number"
											value={(item.salePrice || 0).toFixed(2)}
											className="border-0 quantity-field incrementor"
											disabled
											onChange={(event) =>
												onPriceChange(item._id, item.selectedBatchId, "salePrice", event.target.value)
											}
											{...disableScrollProps}
										/>
										<button
											type="button"
											className="button-plus icon-shape icon-sm btn-light"
											onClick={() =>
												onPriceChange(item._id, item.selectedBatchId, "salePrice", (item.salePrice || 0) + 0.01)
											}
											disabled
										>
											+
										</button>
									</div>
								</td>
								<td className="text-center fw-bold">
									{item.quantity || 0}
								</td>
								<td>
									<div className="input-group custom-qty-input table-price-counter-nowrap">
										<button
											type="button"
											className="button-minus icon-shape icon-sm btn-danger"
											onClick={() =>
												updateReturnQuantity(item._id, Math.max(0, (returnQuantity || 0) - 1), item.quantity || 0)
											}
										>
											-
										</button>
										<input
											type="number"
											value={returnQuantity}
											className="border-0 quantity-field incrementor"
											min={0}
											max={item.quantity || 0}
											onChange={(event) =>
												updateReturnQuantity(item._id, Number(event.target.value), item.quantity || 0)
											}
											{...disableScrollProps}
										/>
										<button
											type="button"
											className="button-plus icon-shape icon-sm btn-primary"
											onClick={() =>
												updateReturnQuantity(item._id, Math.min((returnQuantity || 0) + 1, item.quantity || 0), item.quantity || 0)
											}
										>
											+
										</button>
									</div>
								</td>
								<td className="text-center fw-bold">
									{taxRate || 0}%
								</td>
								<td className="text-center fw-bold">
									{getCurrencySymbol()}{taxableAfterDiscount.toFixed(2)}
								</td>
								{posTaxType === "gst" ? (
									isSameState ? (
										<>
											<td className="text-center text-success">
												{getCurrencySymbol()}{rowCGST.toFixed(2)}
											</td>
											<td className="text-center text-success">
												{getCurrencySymbol()}{rowSGST.toFixed(2)}
											</td>
										</>
									) : (
										<td className="text-center text-success">
											{getCurrencySymbol()}{rowIGST.toFixed(2)}
										</td>
									)
								) : (
									<td className="text-center text-success">
										{getCurrencySymbol()}{rowVAT.toFixed(2)}
									</td>
								)}
								<td className="text-center fw-bold" style={{ fontSize: 'small' }}>
									{getCurrencySymbol()}{grossTotal.toFixed(2)}
								</td>
								{/* <td className="text-center fw-bold" style={{ fontSize: 'small' }}>
									{getCurrencySymbol()}{itemTotal.toFixed(2)}
									<div className="text-muted" style={{ fontSize: "0.8em" }}>
										({getCurrencySymbol()}{itemSubtotal.toFixed(2)} + {getCurrencySymbol()}{itemTax.toFixed(2)})
									</div>
								</td> */}
								<td className="text-center">
									<button
										className="btn-danger btn-sm remove-item"
										onClick={() => onRemoveItem(item._id, item.selectedBatchId)}
									>
										&times;
									</button>
								</td>
							</tr>
						);
					})}
					{cart.length === 0 && (
						<tr>
							<td colSpan="12" className="text-center text-muted">
								{t("No items in cart")}
							</td>
						</tr>
					)}
					{/* {cart.length > 0 && (
						<>
							<tr className="table-light">
								<td colSpan="10" className="text-end fw-bold" style={{ fontSize: 'small' }}>
									{t("Total Return Amount")}:
								</td>
								<td className="text-center fw-bold text-danger" style={{ fontSize: 'small' }}>
									{getCurrencySymbol()}
									{cart.reduce((sum, item) => {
										const returnQty = item.returnQuantity || 0;
										return sum + ((item.salePrice || 0) * returnQty);
									}, 0).toFixed(2)}
								</td>
								<td></td>
							</tr>
						</>
					)} */}
				</tbody>
			</Table>
		</div>
	);
};

export default CartTable;